import { useCallback, useEffect, useRef, useState } from 'react';
import { updateContactRequestStatus } from '../api/updateContactRequestStatusApiService';
import { getAllowedContactRequestNextStatuses } from './useViewContactRequest';
import type { ContactRequestStatus } from '../types/contactRequestTypes';

const NOTES_MAX_LENGTH = 500;

const LABELS = {
  ERROR_REQUIRED: 'To pole jest wymagane',
  ERROR_NOTES_TOO_LONG: 'Notatka nie może przekraczać 500 znaków.',
  UNEXPECTED_ERROR: 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.',
} as const;

interface UseUpdateContactRequestStatusOptions {
  contactRequestId: string | undefined;
  currentStatus: ContactRequestStatus | undefined;
  onSuccess?: () => void;
  onCancelSuccess?: () => void;
}

export interface UseUpdateContactRequestStatusReturn {
  /** Step 1: UpdateStatusModal open state. */
  isOpen: boolean;
  open: () => void;
  close: () => void;
  newStatus: ContactRequestStatus | '';
  setNewStatus: (status: ContactRequestStatus) => void;
  note: string;
  setNote: (value: string) => void;
  newStatusError: string;
  noteError: string;
  isSubmitting: boolean;
  submitError: string;
  allowedStatuses: ContactRequestStatus[];
  canSubmit: boolean;
  /**
   * For non-cancel statuses: calls the API immediately.
   * For `cancelled`: closes step 1 and opens the cancellation confirmation modal.
   */
  submit: () => Promise<void>;

  /** Step 2: CancelContactRequestModal open state. */
  isCancelModalOpen: boolean;
  /**
   * Close the cancellation confirmation modal and re-open step 1 so the user
   * can choose a different status or update the selection.
   */
  closeCancelModal: () => void;
  cancelNote: string;
  setCancelNote: (value: string) => void;
  cancelNoteError: string;
  cancelSubmitError: string;
  submitCancellation: () => Promise<void>;
}

export const useUpdateContactRequestStatus = ({
  contactRequestId,
  currentStatus,
  onSuccess,
  onCancelSuccess,
}: UseUpdateContactRequestStatusOptions): UseUpdateContactRequestStatusReturn => {
  // ── Step 1 state ────────────────────────────────────────────────────────
  const [isOpen, setIsOpen] = useState(false);
  const [newStatus, setNewStatusState] = useState<ContactRequestStatus | ''>(
    ''
  );
  const [note, setNoteState] = useState('');
  const [newStatusError, setNewStatusError] = useState('');
  const [noteError, setNoteError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [submitError, setSubmitError] = useState('');

  // ── Step 2 state ────────────────────────────────────────────────────────
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelNote, setCancelNoteState] = useState('');
  const [cancelNoteError, setCancelNoteError] = useState('');
  const [cancelSubmitError, setCancelSubmitError] = useState('');

  const allowedStatuses = currentStatus
    ? getAllowedContactRequestNextStatuses(currentStatus)
    : [];

  const resetForm = useCallback(() => {
    setNewStatusState('');
    setNoteState('');
    setNewStatusError('');
    setNoteError('');
    setSubmitError('');
  }, []);

  const resetCancelForm = useCallback(() => {
    setCancelNoteState('');
    setCancelNoteError('');
    setCancelSubmitError('');
  }, []);

  const open = useCallback(() => {
    resetForm();
    setIsOpen(true);
  }, [resetForm]);

  const close = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    setIsOpen(false);
    resetForm();
  }, [isSubmitting, resetForm]);

  // Reset form when the contact request or its status changes while the modal
  // is open (e.g. background refresh). Intentionally omits `isOpen` — the
  // open() callback already calls resetForm() before setting isOpen=true, and
  // closeCancelModal() sets isOpen=true to return to step-1 without resetting.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactRequestId, currentStatus]);

  const setNewStatus = useCallback((status: ContactRequestStatus) => {
    setNewStatusState(status);
    setNewStatusError('');
    setNoteState('');
    setNoteError('');
  }, []);

  const setNote = useCallback((value: string) => {
    setNoteState(value);
    if (value.trim().length <= NOTES_MAX_LENGTH) {
      setNoteError('');
    }
  }, []);

  const setCancelNote = useCallback((value: string) => {
    setCancelNoteState(value);
    if (value.trim().length > 0) {
      setCancelNoteError('');
    }
  }, []);

  const canSubmit = !isSubmitting;

  /**
   * Step 1 submit handler.
   *
   * - For `cancelled`: closes UpdateStatusModal and opens the cancellation
   *   confirmation modal (no API call yet).
   * - For all other statuses: validates and calls the status-update API.
   */
  const submit = useCallback(async () => {
    if (!contactRequestId || isSubmittingRef.current) {
      return;
    }

    let hasError = false;
    if (!newStatus) {
      setNewStatusError(LABELS.ERROR_REQUIRED);
      hasError = true;
    }

    // Note field is hidden in the UI when cancelled is selected, so only
    // validate length for non-cancel transitions.
    if (newStatus !== 'cancelled' && note.trim().length > NOTES_MAX_LENGTH) {
      setNoteError(LABELS.ERROR_NOTES_TOO_LONG);
      hasError = true;
    }

    if (hasError) {
      return;
    }

    if (newStatus === 'cancelled') {
      // Two-step flow: hand off to the cancellation confirmation modal.
      setIsOpen(false);
      setIsCancelModalOpen(true);
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const result = await updateContactRequestStatus({
        contactRequestId,
        newStatus: newStatus as ContactRequestStatus,
        note,
      });
      if (result.success) {
        setIsOpen(false);
        resetForm();
        onSuccess?.();
      } else {
        setSubmitError(result.error ?? LABELS.UNEXPECTED_ERROR);
      }
    } catch {
      setSubmitError(LABELS.UNEXPECTED_ERROR);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [contactRequestId, newStatus, note, onSuccess, resetForm]);

  /**
   * Close the cancellation confirmation modal and return to step 1.
   * The selected status (cancelled) is preserved so the user can pick a
   * different status or simply dismiss the flow entirely.
   */
  const closeCancelModal = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    setIsCancelModalOpen(false);
    resetCancelForm();
    setIsOpen(true);
  }, [isSubmitting, resetCancelForm]);

  /**
   * Step 2 submit handler — validates the cancellation reason and calls the
   * status-update API with `new_status: 'cancelled'`.
   */
  const submitCancellation = useCallback(async () => {
    if (!contactRequestId || isSubmittingRef.current) {
      return;
    }

    if (!cancelNote.trim()) {
      setCancelNoteError(LABELS.ERROR_REQUIRED);
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setCancelSubmitError('');
    try {
      const result = await updateContactRequestStatus({
        contactRequestId,
        newStatus: 'cancelled',
        note: cancelNote.trim(),
      });
      if (result.success) {
        setIsCancelModalOpen(false);
        resetForm();
        resetCancelForm();
        (onCancelSuccess ?? onSuccess)?.();
      } else {
        setCancelSubmitError(result.error ?? LABELS.UNEXPECTED_ERROR);
      }
    } catch {
      setCancelSubmitError(LABELS.UNEXPECTED_ERROR);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    contactRequestId,
    cancelNote,
    onSuccess,
    onCancelSuccess,
    resetForm,
    resetCancelForm,
  ]);

  return {
    isOpen,
    open,
    close,
    newStatus,
    setNewStatus,
    note,
    setNote,
    newStatusError,
    noteError,
    isSubmitting,
    submitError,
    allowedStatuses,
    canSubmit,
    submit,
    isCancelModalOpen,
    closeCancelModal,
    cancelNote,
    setCancelNote,
    cancelNoteError,
    cancelSubmitError,
    submitCancellation,
  };
};
